// Shared, render-only receipt add-ons: a tenant custom header line, a tenant custom
// footer line, and the customer running-balance ("now owing") block. Imported by every
// internal sale receipt (POS thermal, New Sale, Sale reprint) so they stay consistent.
//
// Everything here is PLAIN TEXT rendered through React (which escapes it) with
// whiteSpace:'pre-line' to preserve the tenant's line breaks — no HTML injection from the
// free-text fields. Nothing here reads or writes a sale; it only formats values passed in.

// The three settings live as keys on tenants.settings (jsonb) — additive, no schema change.
export type ReceiptExtrasSettings = {
  headerText: string | null
  footerText: string | null
  showBalance: boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function readReceiptExtras(settings: any): ReceiptExtrasSettings {
  const s = settings ?? {}
  const header = typeof s.receipt_header_text === 'string' ? s.receipt_header_text : null
  const footer = typeof s.receipt_footer_text === 'string' ? s.receipt_footer_text : null
  return {
    headerText: header && header.trim() ? header : null,
    footerText: footer && footer.trim() ? footer : null,
    showBalance: s.receipt_show_balance !== false, // default ON
  }
}

// Tenant custom header — printed verbatim ABOVE the business name. Empty → nothing.
export function ReceiptHeaderText({ text }: { text: string | null }) {
  if (!text || !text.trim()) return null
  return (
    <div style={{ textAlign: 'center', whiteSpace: 'pre-line', overflowWrap: 'break-word', fontWeight: 'bold', marginBottom: 4 }}>
      {text}
    </div>
  )
}

// Footer: tenant custom footer (verbatim, if any) then the existing thank-you line.
export function ReceiptFooterText({
  text,
  thankYou = 'Thank you for your business!',
}: {
  text: string | null
  thankYou?: string
}) {
  return (
    <div style={{ textAlign: 'center', overflowWrap: 'break-word' }}>
      {text && text.trim() && (
        <div style={{ whiteSpace: 'pre-line', marginBottom: 4 }}>{text}</div>
      )}
      <div>{thankYou}</div>
    </div>
  )
}

// Customer running-balance block. `nowOwing` is the authoritative getCustomerBalance figure
// (equals that customer's balance on the Customers card); `previous` is derived so the block
// always foots: previous + thisSale − paidNow = nowOwing.
export function CustomerAccountBlock({
  thisSale,
  paidNow,
  nowOwing,
  format,
}: {
  thisSale: number
  paidNow: number
  nowOwing: number
  format: (n: number) => string
}) {
  const previous = nowOwing - (thisSale - paidNow)
  const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: '0 8px', flexWrap: 'wrap' }
  const valStyle: React.CSSProperties = { whiteSpace: 'nowrap', marginLeft: 'auto' }
  return (
    <div style={{ marginTop: 4, overflowWrap: 'break-word' }}>
      <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
      <div style={rowStyle}><span>Previous balance</span><span style={valStyle}>{format(previous)}</span></div>
      <div style={rowStyle}><span>This sale</span><span style={valStyle}>{format(thisSale)}</span></div>
      <div style={rowStyle}><span>Paid now</span><span style={valStyle}>{format(paidNow)}</span></div>
      <div style={{ ...rowStyle, fontWeight: 'bold' }}><span>Total now owing</span><span style={valStyle}>{format(nowOwing)}</span></div>
    </div>
  )
}
